import { useCallback, useMemo, useState } from 'react'
import {
  Autocomplete,
  IconButton,
  InputBase,
  Popper,
  styled,
} from '@mui/material'
import ClearRoundedIcon from '@mui/icons-material/ClearRounded'
import { colorPalette } from '../../../../style/colorPalette'
import { SprintOption } from '../../hooks/sprintOptions'

type Props = {
  value?: string
  options: SprintOption[]
  onChange: (value: string | undefined) => void
  placeholder?: string
  disabled?: boolean
}

// SprintReportNew ヘッダの「開始/終了スプリント」セレクタ。
// ConditionRow の DateBox / GroupKeyBox と同じ薄ボーダー + 白背景に揃え、
// プルダウンはステータス (INPROGRESS/STANDBY/...) でグループ表示する。
export const SprintSelect = ({
  value,
  options,
  onChange,
  placeholder,
  disabled,
}: Props) => {
  const [inputValue, setInputValue] = useState('')

  const selectedOption = useMemo(
    () => options.find(o => o.value === value) ?? null,
    [options, value]
  )

  const handleChange = useCallback(
    (_: unknown, option: SprintOption | null) => {
      onChange(option?.value)
    },
    [onChange]
  )

  const handleInputChange = useCallback((_: unknown, newValue: string) => {
    setInputValue(newValue)
  }, [])

  const clear = useCallback(
    (e: React.MouseEvent) => {
      e.stopPropagation()
      onChange(undefined)
      setInputValue('')
    },
    [onChange]
  )

  return (
    <StyledAutocomplete
      value={selectedOption}
      inputValue={inputValue}
      options={options}
      onChange={handleChange}
      onInputChange={handleInputChange}
      disabled={disabled}
      getOptionLabel={option => option.name}
      groupBy={option => option.status}
      isOptionEqualToValue={(option, val) => option.value === val.value}
      renderInput={params => (
        // DateBox と構造を揃えるため、Autocomplete の renderInput を TextField ベースではなく
        // <InputBase /> 直接にする。これで TextField/FormControl 経由の余計な padding 上書きを避け、
        // 横余白は StyledAutocomplete 側の wrapper padding (DateBox と同じ '0 5px') だけで効くようにする。
        <InputBase
          ref={params.InputProps.ref}
          className={params.InputProps.className}
          inputProps={params.inputProps}
          startAdornment={params.InputProps.startAdornment}
          endAdornment={
            value ? (
              <ClearButton onClick={clear} size="small">
                <ClearRoundedIcon sx={{ fontSize: '14px' }} />
              </ClearButton>
            ) : (
              params.InputProps.endAdornment
            )
          }
          placeholder={placeholder}
          disabled={params.disabled}
          fullWidth={true}
        />
      )}
      renderOption={(props, option) => (
        <li {...props} key={option.value}>
          <OptionContent>
            <OptionName>{option.name}</OptionName>
            {option.subText && <SubText>{option.subText}</SubText>}
          </OptionContent>
        </li>
      )}
      PopperComponent={props => <Popper {...props} placement="bottom-start" />}
      slotProps={{
        paper: {
          sx: {
            '& .MuiAutocomplete-groupLabel': {
              fontWeight: 'bold',
              fontSize: '11px',
              paddingTop: '8px',
              paddingBottom: '4px',
              lineHeight: '1.2',
              top: 0,
              backgroundColor: colorPalette.monotone[1],
            },
            '& .MuiAutocomplete-listbox': {
              paddingTop: 0,
              fontSize: '12px',
            },
          },
        },
      }}
      noOptionsText=""
      size="small"
    />
  )
}

// DateBox と同じ薄ボーダー + 白背景 + 横 padding にして見た目を統一する。
// renderInput で <InputBase /> を直接渡しているので、Autocomplete 由来の特殊な padding 上書きは不要。
const StyledAutocomplete = styled(Autocomplete<SprintOption>)({
  width: '120px',
  backgroundColor: colorPalette.monotone[0],
  border: `1px solid ${colorPalette.monotone[2]}`,
  borderRadius: '4px',
  padding: '0 5px',
  '& .MuiInputBase-root': {
    minHeight: 'unset',
    fontSize: '11px',
  },
  '& .MuiInputBase-input': {
    padding: '0 !important',
    height: '20px',
    lineHeight: '20px',
  },
  // Autocomplete のデフォルト dropdown / clear ボタン (endAdornment) を縦中央に小さく収める。
  '& .MuiAutocomplete-endAdornment': {
    top: '50%',
    transform: 'translateY(-50%)',
  },
  '& .MuiAutocomplete-endAdornment .MuiIconButton-root': {
    padding: '2px',
  },
})

const ClearButton = styled(IconButton)({
  padding: '2px',
})

const OptionContent = styled('div')({
  display: 'flex',
  alignItems: 'center',
  gap: '8px',
})

const OptionName = styled('span')({
  fontSize: '12px',
})

const SubText = styled('span')({
  fontSize: '10px',
  color: colorPalette.monotone[5],
})
